package com.sddamico.swigmore.ds.core

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.ds.PressableStyle
import com.sddamico.swigmore.ds.SwColor
import com.sddamico.swigmore.ds.css
import com.sddamico.swigmore.ds.typeCaps
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

/**
 * One column of a [SortableTable]. [best] orders rows best first; the first click on a header sorts
 * that way and the next reverses it. [sticky] columns stay put while the table scrolls sideways and
 * need a [widthPx].
 */
class TableColumn<T>(
  val label: String,
  val best: Comparator<T>?,
  val widthPx: Int? = null,
  val alignEnd: Boolean = true,
  val sticky: Boolean = false,
  val cell: @Composable (T) -> Unit,
)

/**
 * A data table on a Field card: gold caps headers, hairline rows, and click-to-sort headers. The
 * sorted header turns cream with ▲ (best first) or ▼. Scrolls sideways on narrow screens.
 */
@Composable
fun <T> SortableTable(
  columns: List<TableColumn<T>>,
  rows: List<T>,
  modifier: Modifier = Modifier,
  initialSort: Int = 0,
) {
  var sortColumn by remember { mutableStateOf(initialSort) }
  var reversed by remember { mutableStateOf(false) }
  val sorted = columns[sortColumn].best?.let { best -> rows.sortedWith(if (reversed) best.reversed() else best) } ?: rows
  val stickyOffsets = columns.runningFold(0) { left, column -> if (column.sticky) left + checkNotNull(column.widthPx) else left }

  Card(modifier.css("overflow" to "auto"), variant = CardVariant.Field, padding = "0") {
    Table(attrs = Modifier.css("width" to "100%", "border-collapse" to "separate", "border-spacing" to "0").toAttrs()) {
      Thead {
        Tr {
          columns.forEachIndexed { i, column ->
            val active = i == sortColumn
            Th(
              attrs = Modifier
                .cellBase(column, stickyOffsets[i])
                .css("padding" to "0", "border-bottom" to "1px solid ${SwColor.hairline}", "vertical-align" to "bottom")
                .toAttrs {
                  attr("scope", "col")
                  if (active && column.best != null) attr("aria-sort", if (reversed) "descending" else "ascending")
                }
            ) {
              if (column.best == null) {
                HeaderLabel(column, active = false, arrow = null)
              } else {
                Button(
                  attrs = PressableStyle.toModifier()
                    .css("background" to "none", "border" to "0", "padding" to "0", "margin" to "0", "font" to "inherit",
                      "color" to "inherit", "text-align" to "inherit", "cursor" to "pointer", "display" to "block",
                      "width" to "100%")
                    .toAttrs {
                      onClick {
                        if (active) reversed = !reversed else { sortColumn = i; reversed = false }
                      }
                    }
                ) { HeaderLabel(column, active, arrow = if (!active) null else if (reversed) "▼" else "▲") }
              }
            }
          }
        }
      }
      Tbody {
        sorted.forEach { row ->
          Tr {
            columns.forEachIndexed { i, column ->
              Td(
                attrs = Modifier.cellBase(column, stickyOffsets[i])
                  .css("padding" to "9px 12px", "border-top" to "1px solid ${SwColor.hairlineSoft}",
                    "vertical-align" to "middle", "white-space" to "nowrap")
                  .toAttrs()
              ) { column.cell(row) }
            }
          }
        }
      }
    }
  }
}

@Composable
private fun <T> HeaderLabel(column: TableColumn<T>, active: Boolean, arrow: String?) {
  Div(
    attrs = Modifier.typeCaps("9px", ".18em")
      .css("padding" to "12px", "white-space" to "nowrap", "color" to if (active) SwColor.cream else SwColor.gold)
      .toAttrs()
  ) { Text(if (arrow == null) column.label else "${column.label} $arrow") }
}

private fun <T> Modifier.cellBase(column: TableColumn<T>, left: Int): Modifier =
  css("text-align" to if (column.alignEnd) "right" else "left")
    .then(column.widthPx?.let { Modifier.css("width" to "${it}px", "min-width" to "${it}px") } ?: Modifier)
    .then(
      if (column.sticky) Modifier.css("position" to "sticky", "left" to "${left}px", "z-index" to "1",
        "background" to SwColor.field800)
      else Modifier
    )
